import React, { useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import Thumb from '../../components/Thumb';
import ScanInput from '../../components/ScanInput';
import CameraScanner from '../../components/CameraScanner';
import { getEffectiveSpan, spanCoversDay } from '../../components/ProjectDays';
import {
  Plus,
  Minus,
  Package,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

/**
 * "Packing" tab — Bluetooth scanner driven pack/unpack flow, grouped by source
 * kit + a "direct items" bucket. Per-day filtering applies via `selectedDay`.
 *
 * Each row exposes ±/checkbox controls so the user can pack without the BT
 * scanner if needed; under the hood every action funnels through the same
 * `scannerRef.current.submitCode(code, direction)` that the BT input uses, so
 * inventory math, transactions, and progress all stay in one place.
 *
 * The camera scanner button (📷 Use camera) opens a modal that scans QR codes
 * via the device camera and feeds them into the same submitCode path —
 * field-ready fallback when the BT scanner isn't handy.
 */
export default function PackingTab({ project, selectedDay }) {
  const { items, kits, expandProjectGear, scanForProject } = useApp();
  const [collapsed, setCollapsed] = useState({});
  const [cameraOpen, setCameraOpen] = useState(false);
  const scannerRef = useRef(null);
  const expanded = expandProjectGear(project);

  // Group expanded items by their source kit, plus a "direct" group.
  // Apply per-day filtering: a row is visible only if its source code
  // (kit or 'direct' item) covers `selectedDay`.
  const groups = useMemo(() => {
    const out = { direct: [], kits: {} };
    expanded.forEach((e) => {
      const fromKit = e.sources.find((s) => s.startsWith('kit:'));
      if (fromKit) {
        const kc = fromKit.split(':')[1];
        if (selectedDay != null && !spanCoversDay(getEffectiveSpan(project, kc), selectedDay)) return;
        if (!out.kits[kc]) out.kits[kc] = [];
        out.kits[kc].push(e);
      } else {
        if (selectedDay != null && !spanCoversDay(getEffectiveSpan(project, e.itemCode), selectedDay)) return;
        out.direct.push(e);
      }
    });
    return out;
  }, [expanded, project, selectedDay]);

  // Totals derived from the filtered view so progress reflects the day filter
  const visibleExpanded = [
    ...groups.direct,
    ...Object.values(groups.kits).flat(),
  ];
  const totalUnits = visibleExpanded.reduce((s, e) => s + e.qty, 0);
  const packedUnits = visibleExpanded.reduce(
    (s, e) => s + Math.min(e.qty, project.packed?.[e.itemCode] || 0),
    0,
  );
  const pct = totalUnits === 0 ? 0 : Math.round((packedUnits / totalUnits) * 100);
  const isComplete = totalUnits > 0 && packedUnits === totalUnits;

  const renderRow = (e) => {
    const it = items.find((i) => i.code === e.itemCode);
    if (!it) return null;
    const packed = project.packed?.[e.itemCode] || 0;
    const isFull = packed >= e.qty;
    const isMulti = e.qty > 1;

    // For multi-qty items: explicit −/+ counter so the user can fully clear it.
    // For single-qty items: simple toggle checkbox (click to pack, click again to unpack).
    const control = isMulti ? (
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => scannerRef.current?.submitCode(e.itemCode, 'return')}
          disabled={packed === 0}
          title="Unpack one"
          className={`h-6 w-6 rounded border grid place-items-center transition-colors ${
            packed === 0
              ? 'border-neutral-800 text-neutral-700 cursor-not-allowed'
              : 'border-neutral-700 text-neutral-300 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-300'
          }`}
        >
          <Minus size={11} />
        </button>
        <div
          className={`text-[11px] font-mono min-w-[36px] text-center font-semibold ${
            isFull ? 'text-emerald-400' : packed > 0 ? 'text-amber-300' : 'text-neutral-500'
          }`}
        >
          {packed}/{e.qty}
        </div>
        <button
          onClick={() => scannerRef.current?.submitCode(e.itemCode, 'pack')}
          disabled={isFull}
          title="Pack one"
          className={`h-6 w-6 rounded border grid place-items-center transition-colors ${
            isFull
              ? 'border-neutral-800 text-neutral-700 cursor-not-allowed'
              : 'border-neutral-700 text-neutral-300 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-300'
          }`}
        >
          <Plus size={11} />
        </button>
      </div>
    ) : (
      <button
        onClick={() => {
          scannerRef.current?.submitCode(e.itemCode, isFull ? 'return' : 'pack');
        }}
        title={isFull ? 'Click to unpack' : 'Click to pack'}
        className={`h-5 w-5 rounded shrink-0 border-2 transition-colors grid place-items-center ${
          isFull
            ? 'bg-emerald-500 border-emerald-500'
            : 'border-neutral-700 hover:border-neutral-500'
        }`}
      >
        {isFull && <CheckCircle2 size={12} className="text-neutral-950" />}
      </button>
    );

    return (
      <div key={e.itemCode} className="px-4 py-2.5 flex items-center gap-3 row-hover">
        {control}
        <Thumb src={it.image} name={it.name} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm truncate ${isFull ? 'text-neutral-500 line-through' : 'text-white'}`}>
            {it.name}
          </div>
          <div className="text-[11px] text-neutral-500">
            <span className="font-mono">{it.code}</span>
          </div>
        </div>
        {!isMulti && (
          <div
            className={`text-[11px] font-medium ${
              isFull ? 'text-emerald-400' : 'text-neutral-400'
            }`}
          >
            {packed} / {e.qty}
          </div>
        )}
        {isMulti && (
          <div
            className={`text-[11px] font-medium ${
              isFull ? 'text-emerald-400' : packed > 0 ? 'text-amber-300' : 'text-neutral-500'
            }`}
          >
            {packed} / {e.qty}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="surface rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] tracking-wider uppercase text-neutral-500">Packing progress</div>
          <div className="text-xs text-neutral-300 font-medium">
            {packedUnits} / {totalUnits} units · {pct}%
          </div>
        </div>
        <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-500 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Scan input + camera fallback */}
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <ScanInput
            ref={scannerRef}
            direction="pack"
            isComplete={isComplete}
            onScan={(code, dir) => scanForProject(project.id, code, dir || 'pack')}
          />
        </div>
        <button
          onClick={() => setCameraOpen(true)}
          title="Use this device's camera as a QR scanner — fallback for when the Bluetooth scanner isn't handy"
          className="shrink-0 px-3 rounded-md border border-neutral-800 text-neutral-300 hover:text-amber-300 hover:border-amber-500/40 hover:bg-amber-500/5 flex items-center gap-1.5 text-[11px] transition-colors"
          data-testid="packing-camera-btn"
        >
          <Camera size={13} />
          <span className="hidden sm:inline">Use camera</span>
        </button>
      </div>

      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={(code) => {
          // Feed the camera scan into exactly the same path as a BT scan.
          // Direction "pack" by default; submitCode will toggle if already full.
          scannerRef.current?.submitCode(code, 'pack');
        }}
      />

      {expanded.length === 0 && (
        <div className="surface rounded-lg p-10 text-center text-sm text-neutral-500">
          Add gear in the “Gear List” tab to start packing.
        </div>
      )}

      {Object.entries(groups.kits).map(([kitCode, entries]) => {
        const kit = kits.find((k) => k.code === kitCode);
        const isCollapsed = collapsed[kitCode];
        const kitPacked = entries.every(
          (e) => (project.packed?.[e.itemCode] || 0) >= e.qty,
        );
        return (
          <div key={kitCode} className="surface rounded-lg overflow-hidden">
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [kitCode]: !c[kitCode] }))}
              className="w-full px-4 py-2.5 border-b border-neutral-900 bg-neutral-950 flex items-center gap-2 text-left hover:bg-neutral-900"
            >
              {isCollapsed ? <ChevronRight size={13} className="text-neutral-500" /> : <ChevronDown size={13} className="text-neutral-500" />}
              <Package size={13} className={kitPacked ? 'text-emerald-400' : 'text-amber-400'} />
              <span className="text-xs font-semibold text-white">{kit?.name || kitCode}</span>
              <span className="text-[11px] text-neutral-500 ml-auto">
                {entries.filter((e) => (project.packed?.[e.itemCode] || 0) >= e.qty).length} / {entries.length}
              </span>
            </button>
            {!isCollapsed && <div className="divide-y divide-neutral-900">{entries.map(renderRow)}</div>}
          </div>
        );
      })}

      {groups.direct.length > 0 && (
        <div className="surface rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-neutral-900 bg-neutral-950 flex items-center gap-2">
            <Camera size={13} className="text-amber-400" />
            <span className="text-xs font-semibold text-white">Individual Items</span>
            <span className="text-[11px] text-neutral-500 ml-auto">{groups.direct.length}</span>
          </div>
          <div className="divide-y divide-neutral-900">{groups.direct.map(renderRow)}</div>
        </div>
      )}
    </div>
  );
}
